import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { FiSearch, FiSave, FiGlobe, FiTag, FiFileText } from "react-icons/fi";
import toast from "react-hot-toast";

const PAGE_KEYS = [
    { id: "home", label: "Home Page", path: "/" },
    { id: "shop", label: "Shop / Products", path: "/shop" },
    { id: "blog", label: "Blog / News", path: "/news" },
    { id: "about", label: "About Page", path: "/about" },
    { id: "contact", label: "Contact Page", path: "/contact" },
    { id: "cart", label: "Cart Page", path: "/cart" },
    { id: "local-bookstores", label: "Local Bookstores", path: "/local-bookstores" },
    { id: "default", label: "Global Default (Fallback)", path: "*" },
];

const EMPTY_PAGE_SEO = {
    title: "",
    description: "",
    keywords: "",
};

export default function AdminSEO() {
    const [seoData, setSeoData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activePage, setActivePage] = useState("home");

    useEffect(() => {
        async function load() {
            try {
                const snap = await getDoc(doc(db, "siteConfig", "seo"));
                if (snap.exists()) {
                    setSeoData(snap.data().pages || {});
                }
            } catch (err) {
                console.error("Error loading SEO:", err);
                toast.error("Failed to load SEO settings.");
            }
            setLoading(false);
        }
        load();
    }, []);

    const currentPageData = seoData[activePage] || { ...EMPTY_PAGE_SEO };

    function handleChange(e) {
        const { name, value } = e.target;
        setSeoData(prev => ({
            ...prev,
            [activePage]: {
                ...(prev[activePage] || { ...EMPTY_PAGE_SEO }),
                [name]: value
            }
        }));
    }

    async function handleSave() {
        setSaving(true);
        try {
            await setDoc(doc(db, "siteConfig", "seo"), {
                pages: seoData,
                updatedAt: serverTimestamp()
            }, { merge: true });
            toast.success("SEO settings updated!");
        } catch (err) {
            console.error("Save error:", err);
            toast.error("Failed to save SEO settings.");
        }
        setSaving(false);
    }

    if (loading) return <div className="admin-loading">Loading SEO configuration...</div>;

    return (
        <div className="admin-section">
            <div className="admin-section-header">
                <div>
                    <h2 style={{ margin: 0 }}>Search Engine Optimization</h2>
                    <p style={{ color: "#888", margin: "4px 0 0", fontSize: 14 }}>Manage how your site appears in Google and social media.</p>
                </div>
                <button className="admin-btn-primary" onClick={handleSave} disabled={saving}>
                    <FiSave /> {saving ? "Saving..." : "Save SEO"}
                </button>
            </div>

            <div className="admin-seo-grid">
                {/* Sidebar - Page Selection */}
                <div className="admin-card" style={{ padding: 15 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: "#999", textTransform: "uppercase", marginBottom: 15, paddingLeft: 10 }}>Select Page</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {PAGE_KEYS.map(p => (
                            <button
                                key={p.id}
                                onClick={() => setActivePage(p.id)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: "12px 15px",
                                    borderRadius: 8,
                                    border: "none",
                                    background: activePage === p.id ? "var(--accent-light, #f0e6d2)" : "transparent",
                                    color: activePage === p.id ? "var(--accent, #c2a35d)" : "#555",
                                    fontWeight: activePage === p.id ? 700 : 500,
                                    textAlign: "left",
                                    cursor: "pointer",
                                    transition: "0.2s"
                                }}
                            >
                                <FiGlobe size={16} />
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Main Form */}
                <div className="admin-card" style={{ padding: 30 }}>
                    <div style={{ marginBottom: 25, borderBottom: "1px solid #eee", paddingBottom: 15 }}>
                        <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
                            <FiSearch color="var(--accent)" /> {PAGE_KEYS.find(p => p.id === activePage)?.label}
                        </h3>
                        <p style={{ color: "#888", fontSize: 13, marginTop: 5 }}>Path: <code>{PAGE_KEYS.find(p => p.id === activePage)?.path}</code></p>
                    </div>

                    <div className="admin-form-field">
                        <label>Meta Title</label>
                        <input
                            name="title"
                            value={currentPageData.title}
                            onChange={handleChange}
                            placeholder="e.g. Home | LB Books - Historias Audaces"
                            maxLength={70}
                        />
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                            <span style={{ fontSize: 11, color: "#888" }}>Recommended: 50-60 characters</span>
                            <span style={{ fontSize: 11, color: currentPageData.title.length > 60 ? "#d93025" : "#888" }}>{currentPageData.title.length}/70</span>
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Meta Description</label>
                        <textarea
                            name="description"
                            value={currentPageData.description}
                            onChange={handleChange}
                            placeholder="Enter a brief summary of this page for search results..."
                            rows={4}
                            style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #eee", fontFamily: "inherit", fontSize: 14 }}
                            maxLength={160}
                        />
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                            <span style={{ fontSize: 11, color: "#888" }}>Recommended: 150-160 characters</span>
                            <span style={{ fontSize: 11, color: currentPageData.description.length > 160 ? "#d93025" : "#888" }}>{currentPageData.description.length}/160</span>
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Keywords (Keyboards)</label>
                        <textarea
                            name="keywords"
                            value={currentPageData.keywords}
                            onChange={handleChange}
                            placeholder="e.g. books, liliz black, stories, fantasy, porto rico"
                            rows={2}
                            style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #eee", fontFamily: "inherit", fontSize: 14 }}
                        />
                        <p style={{ fontSize: 11, color: "#888", marginTop: 5 }}>Comma separated list of keywords relevant to this page.</p>
                    </div>

                    {/* Preview Section */}
                    <div style={{ marginTop: 40, padding: 20, background: "#f8f9fa", borderRadius: 12, border: "1px solid #eee" }}>
                        <p style={{ fontSize: 12, fontWeight: 700, color: "#999", textTransform: "uppercase", marginBottom: 15 }}>Google Search Preview</p>
                        <div style={{ maxWidth: 600 }}>
                            <p style={{ color: "#1a0dab", fontSize: 18, margin: "0 0 4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {currentPageData.title || "Page Title Sample"}
                            </p>
                            <p style={{ color: "#006621", fontSize: 14, margin: "0 0 4px" }}>
                                {window.location.origin}{PAGE_KEYS.find(p => p.id === activePage)?.path}
                            </p>
                            <p style={{ color: "#545454", fontSize: 13, lineHeight: "1.4", margin: 0 }}>
                                {currentPageData.description || "Enter a meta description to see how this page might appear in search engine results."}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
